import { supabase } from '../lib/supabaseClient';

// VAPID public key - this will be generated and stored in env
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';

interface PushSubscriptionData {
  endpoint: string;
  p256dh_key: string;
  auth_key: string;
  user_agent: string;
  platform: 'ios' | 'android' | 'desktop' | 'unknown';
}

interface NotificationPreferences {
  push_enabled: boolean;
  messages_enabled: boolean;
  vybe_activity_enabled: boolean;
  applications_enabled: boolean;
  reminders_enabled: boolean;
}

// Convert base64 string to Uint8Array for VAPID key
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Detect platform
function detectPlatform(): 'ios' | 'android' | 'desktop' | 'unknown' {
  const ua = navigator.userAgent.toLowerCase();

  if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod')) {
    return 'ios';
  }
  if (ua.includes('android')) {
    return 'android';
  }
  if (ua.includes('windows') || ua.includes('mac') || ua.includes('linux')) {
    return 'desktop';
  }
  return 'unknown';
}

// Check if push notifications are supported
export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator &&
         'PushManager' in window &&
         'Notification' in window;
}

// Check current notification permission
export function getNotificationPermission(): NotificationPermission {
  if (!('Notification' in window)) {
    return 'default';
  }
  return Notification.permission;
}

// Request notification permission
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    throw new Error('Notifications not supported');
  }

  const permission = await Notification.requestPermission();
  return permission;
}

// Subscribe to push notifications
export async function subscribeToPush(userId: string): Promise<PushSubscription | null> {
  if (!isPushSupported()) {
    throw new Error('Push notifications not supported');
  }

  if (!VAPID_PUBLIC_KEY) {
    console.error('VAPID public key not configured');
    throw new Error('Push notifications not configured');
  }

  // Request permission first
  const permission = await requestNotificationPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission denied');
  }

  // Get service worker registration
  const registration = await navigator.serviceWorker.ready;

  // Check if already subscribed
  let subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    // Subscribe to push
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });
    } catch (error) {
      console.error('Failed to subscribe to push:', error);
      throw error;
    }
  }

  // Save subscription to database
  if (subscription) {
    await savePushSubscription(userId, subscription);
  }

  return subscription;
}

// Save push subscription to database
async function savePushSubscription(userId: string, subscription: PushSubscription): Promise<void> {
  const subscriptionJSON = subscription.toJSON();

  const data: PushSubscriptionData = {
    endpoint: subscription.endpoint,
    p256dh_key: subscriptionJSON.keys?.p256dh || '',
    auth_key: subscriptionJSON.keys?.auth || '',
    user_agent: navigator.userAgent,
    platform: detectPlatform()
  };

  const { error } = await supabase
    .from('push_subscriptions')
    .upsert({
      user_id: userId,
      ...data,
      is_active: true,
      last_used_at: new Date().toISOString()
    }, {
      onConflict: 'endpoint'
    });

  if (error) {
    console.error('Failed to save push subscription:', error);
    throw error;
  }
}

// Unsubscribe from push notifications
export async function unsubscribeFromPush(userId: string): Promise<void> {
  if (!isPushSupported()) {
    return;
  }

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  if (subscription) {
    await subscription.unsubscribe();

    // Mark subscription as inactive in database
    const { error } = await supabase
      .from('push_subscriptions')
      .update({ is_active: false })
      .eq('user_id', userId)
      .eq('endpoint', subscription.endpoint);

    if (error) {
      console.error('Failed to update push subscription status:', error);
    }
  }
}

// Get user's notification preferences
export async function getNotificationPreferences(userId: string): Promise<NotificationPreferences | null> {
  const { data, error } = await supabase
    .from('notification_preferences')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('Failed to get notification preferences:', error);
    return null;
  }

  return data;
}

// Update notification preferences
export async function updateNotificationPreferences(
  userId: string,
  preferences: Partial<NotificationPreferences>
): Promise<void> {
  const { error } = await supabase
    .from('notification_preferences')
    .upsert({
      user_id: userId,
      ...preferences,
      updated_at: new Date().toISOString()
    }, {
      onConflict: 'user_id'
    });

  if (error) {
    console.error('Failed to update notification preferences:', error);
    throw error;
  }
}

// Enable push notifications
export async function enablePushNotifications(userId: string): Promise<void> {
  await subscribeToPush(userId);
  await updateNotificationPreferences(userId, { push_enabled: true });
}

// Disable push notifications
export async function disablePushNotifications(userId: string): Promise<void> {
  await unsubscribeFromPush(userId);
  await updateNotificationPreferences(userId, { push_enabled: false });
}

// Check if user has active push subscription
export async function hasActivePushSubscription(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('push_subscriptions')
    .select('id')
    .eq('user_id', userId)
    .eq('is_active', true)
    .maybeSingle();

  if (error) {
    console.error('Failed to check push subscription:', error);
    return false;
  }

  return !!data;
}

// Send test notification
export async function sendTestNotification(): Promise<void> {
  if (getNotificationPermission() !== 'granted') {
    throw new Error('Notification permission not granted');
  }

  const registration = await navigator.serviceWorker.ready;

  await registration.showNotification('VybeTribe Test', {
    body: 'Push notifications are working!',
    icon: '/pwa-192x192.png',
    badge: '/pwa-192x192.png',
    tag: 'test-notification',
    vibrate: [200, 100, 200]
  });
}
